// Single source of truth for two closely-related admin-dashboard questions:
//   1. "Does this order count as real money?" (revenue / volume / fees / earnings)
//   2. "Is this a song the fan selected but never paid for?" (the relabel)
//
// Background: an order's `paid` flag (true once the fan's card is charged) is
// INDEPENDENT of its `status` (the fulfillment lifecycle: pending → queued /
// accepted → played; or declined; plus refunded / frozen / expired). A song a
// fan selected but never paid for is stored as { status: "pending", paid:
// false } — valuable intent / Trending data, but NOT revenue.
//
// "Counts as money" = the fan paid real money AND it wasn't returned:
//   paid === true  AND  amount > 0  AND  status not in { declined, refunded, frozen, expired }
// A FREE request or playlist is stored { paid: true, amount: 0 } ("nothing
// owed") — a real request, but not money. With the free tier those are common.
// declined / refunded = money refunded to the fan; frozen / expired = never
// captured (these already carry paid:false, but they're listed here too so the
// rule stays correct and self-documenting even if that ever changes).
//
// IMPORTANT: keep this rule in sync with countsAsRevenue() in
// server/lib/founderMoney.js — both sides must agree, or the per-DJ figures
// (computed server-side) will drift from the dashboard leaderboards.

export interface OrderMoneyFields {
  paid?: boolean;
  status?: string | null;
  amount?: number | null;
}

// Statuses where the fan's money was returned or never collected. An order in
// any of these is never revenue, regardless of the `paid` flag.
const NON_REVENUE_STATUSES = new Set(["declined", "refunded", "frozen", "expired"]);

/** True when the order represents real money the platform / talent kept. */
export function countsAsRevenue(o: OrderMoneyFields): boolean {
  return o.paid === true && (o.amount ?? 0) > 0 && !NON_REVENUE_STATUSES.has(o.status ?? "");
}

export interface OrderFeeFields {
  amount: number;
  totalAmount?: number | null;
  feeAmount?: number | null;
  djNetShare?: number | null;
}

/**
 * Our commission on one paid order — the same chain as feeOf() in
 * server/lib/founderMoney.js (keep the two in sync): 0 when the talent kept
 * the whole bid (djNetShare ≥ amount — the 1.5.1 launch offer, a 0% custom
 * rate: the fan's card-fee cushion on top pays Stripe, it isn't ours), else
 * feeAmount (>0), else totalAmount − amount, else the legacy 15% estimate.
 */
export function commissionOf(o: OrderFeeFields): number {
  const amount = o.amount || 0;
  if (typeof o.djNetShare === "number" && o.djNetShare >= amount) return 0;
  if (typeof o.feeAmount === "number" && o.feeAmount > 0) return o.feeAmount;
  if (typeof o.totalAmount === "number" && o.totalAmount > amount) return o.totalAmount - amount;
  return (amount * 0.15) / 0.85;
}

/**
 * True when the order is a song the fan SELECTED but never paid for — the
 * intent-only case. These are always stored as status "pending" with
 * paid:false. (An unpaid order that later aged out becomes "expired", which is
 * a distinct state and is NOT a live selection.)
 */
export function isUnpaidSelection(o: OrderMoneyFields): boolean {
  return o.paid !== true && (o.status ?? "") === "pending";
}

/**
 * Admin-facing status label. An unpaid "pending" order reads "selected" (a song
 * the fan picked but never paid for). Everything else shows its raw status
 * unchanged — a PAID pending order (awaiting the DJ) stays "pending", and
 * "accepted" / "queued" / "played" / "declined" are untouched.
 */
export function adminStatusLabel(o: OrderMoneyFields): string {
  if (isUnpaidSelection(o)) return "selected";
  return o.status || "—";
}

/**
 * CSS modifier for the status badge / dot. Unpaid selections get their own
 * "selected" treatment; otherwise we keep the existing per-status class.
 */
export function adminStatusClass(o: OrderMoneyFields): string {
  if (isUnpaidSelection(o)) return "selected";
  return o.status || "pending";
}
